import EditNoteIcon from '@assets/icons/edit-note.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useRef, useState } from 'react';
import TrashMedium from '@assets/icons/trash-small-thin.svg';
import { BORDER, focusedItem } from '@/styles';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { DefaultOrderableItemListItemActions } from './orderable-item-list-item-actions';
import { displayLineItemPrice, formatCurrency } from '@/utils/money';
import { DSRToolingActionSheet } from './dsr-tooling-action-sheet';
import {
  GestureResponderEvent,
  Keyboard,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { OrderableItemLabels } from './orderable-item-labels';
import { OrderItemNote } from '@/components/order-item-note';
import { OrderItemTopRow } from './order-item-top-row';
import { OrderSummaryListItemHoverAction } from './order-summary-list-item-hover-action';
import { PromotionLabelType } from './promotion-label';
import { SalesRepInfo } from './sales-rep-info';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useHover } from 'react-native-web-hooks';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';
import { useNavigateToDSRTooling } from '@/hooks/use-dsr-tooling-navigation';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { ZeroQuantityBehavior } from '@/components/quantity-selector';

export interface OrderSummaryItemPromotions {
  discountInMicros: number;
  promotionLabels: PromotionLabelType[];
}

interface Props {
  availableQuantityMap: { [variantPackUUID: string]: number | undefined };
  isDescriptionVisible: boolean;
  isFocused: boolean;
  isReplacementItem: boolean;
  loyaltyPoints?: number;
  onEditNotes: () => void;
  onSalesRepPriceUpdated: () => void;
  option: ItemListOption;
  promotions?: OrderSummaryItemPromotions;
  removeItems: (variantPackUUIDs: string[]) => Promise<void>;
  resetSubstitutionItem: () => Promise<void>;
  updateQuantity: UpdateQuantityFn;
}

export const OrderSummaryListItem: React.FC<Props> = ({
  availableQuantityMap,
  isDescriptionVisible,
  isFocused,
  isReplacementItem,
  onEditNotes,
  onSalesRepPriceUpdated,
  option,
  promotions,
  loyaltyPoints,
  removeItems,
  resetSubstitutionItem,
  updateQuantity,
}) => {
  const quantity = useItemVisibleQuantity(option.metadata!.variantPackUUID);
  const isKeyboardVisible = useKeyboardVisibility();
  const salesRepMode = useSalesRepMode();
  const { selectItemListOption } = useItemDetailContext();
  const [styles] = useStyles();
  const navigateToDSRTooling = useNavigateToDSRTooling();
  const [
    dsrToolingActionSheetItemMetadata,
    setDSRToolingActionSheetItemMetadata,
  ] = useState<ItemListOptionMetadata | null>(null);

  const theme = useTheme();
  const hoverTargetRef = useRef(null);
  const isTextHovered = useHover(hoverTargetRef);

  const metadata = option.metadata!;
  const description = isDescriptionVisible ? metadata.description : null;

  const handleAddNotePress = useCallback(
    (event: GestureResponderEvent) => {
      event.stopPropagation();
      onEditNotes();
    },
    [onEditNotes],
  );

  const handleRemove = useCallback(
    async (event?: GestureResponderEvent) => {
      event?.stopPropagation();
      if (isReplacementItem) {
        await resetSubstitutionItem();
      } else {
        await removeItems([metadata.variantPackUUID]);
      }
    },
    [
      isReplacementItem,
      metadata.variantPackUUID,
      removeItems,
      resetSubstitutionItem,
    ],
  );

  const isOrderNoteEnabled = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  )?.enabled;
  const canWriteLineItemRequest = useCanWriteLineItemRequest();

  const hoverActions =
    Platform.OS === 'web' && isTextHovered ? (
      <View style={styles.hoverActions}>
        {isOrderNoteEnabled && canWriteLineItemRequest ? (
          <OrderSummaryListItemHoverAction
            accessibilityLabel={i18n.t('add_note')}
            Icon={EditNoteIcon}
            iconProps={{ fill: theme.colors.primary }}
            onPress={handleAddNotePress}
            trackingData={{
              name: 'order_summary_list_item.add_note_button',
              properties: {
                variantPackUUID: metadata.variantPackUUID,
              },
            }}
          />
        ) : null}
        <OrderSummaryListItemHoverAction
          accessibilityLabel={i18n.t('remove_item')}
          Icon={TrashMedium}
          iconProps={{ stroke: theme.colors.primary }}
          onPress={handleRemove}
          trackingData={{
            name: 'order_summary_list_item.remove_button',
            properties: {
              variantPackUUID: metadata.variantPackUUID,
            },
          }}
        />
      </View>
    ) : null;

  return (
    <>
      <TouchableOpacity
        activeOpacity={isKeyboardVisible ? 1 : undefined}
        onPress={() => {
          if (isKeyboardVisible) {
            Keyboard.dismiss();
            return;
          }

          selectItemListOption(
            option,
            { source: 'order_summary' },
            { updateQuantity },
          );
        }}
        style={[styles.base, isFocused ? focusedItem() : null]}
        testID={`summary-list-item:${option.metadata?.variantPackUUID}`}
      >
        <OrderableItemLabels
          option={option}
          // Coalesce to an empty array so regular promotion labels aren't displayed
          promotionLabelOverrides={promotions?.promotionLabels ?? []}
        />
        <OrderItemTopRow
          description={description || null}
          formattedDiscount={
            typeof promotions?.discountInMicros === 'number' &&
            promotions?.discountInMicros > 0
              ? formatCurrency(
                  promotions.discountInMicros,
                  metadata.currencyCode,
                )
              : null
          }
          formattedPrice={displayLineItemPrice({
            currencyCode: metadata.currencyCode,
            quantity,
            unitCount: metadata.unitCount,
            unitPriceInMicros:
              metadata.promotionPricePerUnitInMicros ??
              metadata.unitPriceInMicros,
          })}
          hoverTargetRef={hoverTargetRef}
          loyaltyPointsLabel={
            loyaltyPoints && loyaltyPoints > 0
              ? i18n.t('loyalty.points_per_item', {
                  points: loyaltyPoints.toLocaleString(i18n.locale),
                })
              : null
          }
          name={option.title}
          onRemove={handleRemove}
          rightContent={hoverActions}
        >
          <View style={BORDER.primary}>
            <DefaultOrderableItemListItemActions
              isFocused={isFocused}
              metadata={metadata}
              quantity={quantity}
              quantityInStock={availableQuantityMap[metadata.variantPackUUID]}
              updateQuantity={async (updateQuantityData) => {
                if (updateQuantityData.newQuantity === 0 && isReplacementItem) {
                  await resetSubstitutionItem();
                } else {
                  updateQuantity(updateQuantityData);
                }
              }}
              zeroQuantityBehavior={ZeroQuantityBehavior.LOADING}
            />
            {salesRepMode ? (
              <SalesRepInfo
                onPress={() => {
                  // On mobile devices navigate to the screen, on desktop/tablet
                  // devices, open an action sheet so that its on top of the modal
                  const didNavigate = navigateToDSRTooling({
                    itemMetadata: metadata,
                    onSalesRepPriceUpdated,
                    updateQuantity,
                  });
                  if (!didNavigate) {
                    setDSRToolingActionSheetItemMetadata(metadata);
                  }
                }}
                onSalesRepPriceUpdated={onSalesRepPriceUpdated}
                variantPackUUID={metadata.variantPackUUID}
              />
            ) : null}
          </View>
          {metadata.notes ? (
            <View style={styles.noteContainer}>
              <OrderItemNote
                notes={metadata.notes}
                onPress={onEditNotes}
                trackingData={{
                  name: 'order_summary_list_item.edit_note_button',
                }}
              />
            </View>
          ) : null}
        </OrderItemTopRow>
      </TouchableOpacity>
      {dsrToolingActionSheetItemMetadata ? (
        <DSRToolingActionSheet
          itemMetadata={dsrToolingActionSheetItemMetadata}
          onClose={() => {
            setDSRToolingActionSheetItemMetadata(null);
          }}
          onSalesRepPriceUpdated={onSalesRepPriceUpdated}
          updateQuantity={updateQuantity}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    padding: 8,
  },
  hoverActions: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 16,
  },
  noteContainer: {
    marginTop: theme.sk.spacing.spaceSmall,
  },
}));
